/** Administration → Access: who is signed in, signing sessions out, and the idle timeout. */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, LogOut, MonitorSmartphone } from 'lucide-react';
import { api, errorMessage } from '../../api/client';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';
import { browserLabel, relative } from '../../lib/format';
import { queryKeys } from '../../api/queryKeys';

interface SessionRow {
  id: string;
  user: { id: string; displayName: string; email: string };
  role: string;
  roleName: string;
  signedInAt: string;
  lastActiveAt: string;
  userAgent: string | null;
  ip: string | null;
  current: boolean;
}

const IDLE_CHOICES = [0, 15, 30, 60, 120, 240, 480];
const idleLabel = (m: number) => (m === 0 ? 'Off' : m < 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? '' : 's'}`);

export function SignedInAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['sessions'], queryFn: () => api<{ idleMinutes: number; items: SessionRow[] }>('/api/v1/sessions'), refetchInterval: 30_000 });
  const [idle, setIdle] = useState<string>('');
  useEffect(() => { if (q.data && idle === '') setIdle(String(q.data.idleMinutes)); }, [q.data, idle]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['sessions'] });

  const saveIdle = async () => {
    const minutes = Number(idle);
    try {
      await api('/api/v1/system/session-settings', { method: 'PUT', json: { idleMinutes: minutes } });
      toast(minutes ? `People are signed out after ${idleLabel(minutes)} without activity` : 'The idle timeout is off');
      await refresh();
      void qc.invalidateQueries({ queryKey: queryKeys.me() });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const signOut = async (s: SessionRow) => {
    if (!window.confirm(`Sign ${s.user.displayName} out of ${browserLabel(s.userAgent)}?`)) return;
    try {
      await api(`/api/v1/sessions/${s.id}`, { method: 'DELETE' });
      toast('Signed out');
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const signOutEverywhere = async (s: SessionRow) => {
    if (!window.confirm(`Sign ${s.user.displayName} out everywhere? Every browser they use PRISM in needs to sign in again.`)) return;
    try {
      const r = await api<{ sessions: number }>(`/api/v1/members/${s.user.id}/sign-out`, { method: 'POST', json: {} });
      toast(`${s.user.displayName} signed out of ${r.sessions} session${r.sessions === 1 ? '' : 's'}`);
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const items = q.data?.items ?? [];
  const choices = q.data && !IDLE_CHOICES.includes(q.data.idleMinutes) ? [...IDLE_CHOICES, q.data.idleMinutes].sort((a, b) => a - b) : IDLE_CHOICES;
  // Signing yourself out everywhere would end this session too: use Sign out on your other sessions instead.
  const selfId = items.find((s) => s.current)?.user.id;
  return (
    <section className="panel-card" style={{ marginBottom: 20 }} data-testid="signed-in-users">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ flex: 1, margin: 0 }}><MonitorSmartphone size={16} color="var(--accent-strong)" /> Signed-in users</h3>
        <label className="muted" htmlFor="idle-timeout" style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}><Clock size={14} /> Sign out after inactivity</label>
        <select id="idle-timeout" className="select" value={idle} onChange={(e) => setIdle(e.target.value)} data-testid="idle-timeout" style={{ width: 'auto', minWidth: 140, flex: 'none' }}>
          {choices.map((m) => <option key={m} value={String(m)}>{idleLabel(m)}</option>)}
        </select>
        <button className="btn sm" disabled={!q.data || idle === String(q.data.idleMinutes)} onClick={() => void saveIdle()} data-testid="save-idle-timeout">Save</button>
      </div>
      <p className="muted" style={{ marginTop: 6 }}>
        Activity is typing, clicking, scrolling, playing media, and saving changes; a tab left open on its own does not count. People are warned one minute before they are signed out.
      </p>
      <TableScroll>
      <table className="table compact" data-testid="sessions">
        <thead><tr><th>User</th><th>Role</th><th>Signed in</th><th>Last active</th><th>Browser</th><th>IP</th><th /></tr></thead>
        <tbody>
          {items.map((s) => (
            <tr key={s.id} data-session-user={s.user.email}>
              <td className="wrap" style={{ minWidth: 160 }}><div style={{ fontWeight: 560 }}>{s.user.displayName}{s.current && <span className="tag" style={{ marginLeft: 6 }}>This session</span>}</div><div className="faint">{s.user.email}</div></td>
              <td className="muted wrap">{s.roleName}</td>
              <td className="muted nowrap" title={new Date(s.signedInAt).toLocaleString()}>{relative(s.signedInAt)}</td>
              <td className="muted nowrap" title={new Date(s.lastActiveAt).toLocaleString()}>{relative(s.lastActiveAt)}</td>
              <td className="muted wrap" title={s.userAgent ?? undefined}>{browserLabel(s.userAgent)}</td>
              <td className="muted mono nowrap">{s.ip ?? '—'}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'normal', minWidth: 120 }}>
                {!s.current && (
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button className="btn ghost sm" onClick={() => void signOut(s)} data-testid="session-sign-out"><LogOut size={13} /> Sign out</button>
                    {s.user.id !== selfId && <button className="btn ghost sm" onClick={() => void signOutEverywhere(s)} data-testid="session-sign-out-everywhere">Sign out everywhere</button>}
                  </div>
                )}
              </td>
            </tr>
          ))}
          {!items.length && <tr><td colSpan={7} className="muted">{q.isLoading ? 'Loading…' : 'Nobody is signed in.'}</td></tr>}
        </tbody>
      </table>
      </TableScroll>
    </section>
  );
}
